import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { DetailPageHeader } from '../../components/common/DetailPageLayout';

function fmt(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

export default function AdminDbBackupDeletionQueue() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [rejectOpen, setRejectOpen] = useState({ open: false, row: null });
  const [approveOpen, setApproveOpen] = useState({ open: false, row: null });
  const [notes, setNotes] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/db-backup/deletion-requests');
      setRows(Array.isArray(r.data?.data) ? r.data.data : []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load requests');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const approve = async () => {
    const row = approveOpen.row;
    if (!row) return;
    try {
      await api.post(`/admin/db-backup/deletion-requests/${row.id}/approve`, { review_notes: notes || null });
      toast.success('Approved; backup folder removed.');
      setApproveOpen({ open: false, row: null });
      setNotes('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Approve failed');
    }
  };

  const reject = async () => {
    const row = rejectOpen.row;
    if (!row) return;
    try {
      await api.post(`/admin/db-backup/deletion-requests/${row.id}/reject`, { review_notes: notes || null });
      toast.success('Request rejected.');
      setRejectOpen({ open: false, row: null });
      setNotes('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Reject failed');
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <DetailPageHeader title="DB backup deletion requests" onBack={() => navigate(-1)} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Only backups outside the <strong>3 most recent</strong> may be requested for deletion. A <strong>different</strong>{' '}
        administrator than the requester must approve or reject.
      </Typography>
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ mb: 2 }}>
            <Button variant="outlined" size="small" onClick={load} disabled={loading}>
              Refresh
            </Button>
          </Box>
          {loading ? (
            <CircularProgress />
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Folder</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Requester</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.folder_name}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={r.status}
                        color={r.status === 'pending' ? 'warning' : r.status === 'completed' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{r.requester_email || '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>{r.reason}</TableCell>
                    <TableCell>{fmt(r.created_at)}</TableCell>
                    <TableCell align="right">
                      {r.status === 'pending' && (
                        <>
                          <Button
                            size="small"
                            color="success"
                            onClick={() => {
                              setNotes('');
                              setApproveOpen({ open: true, row: r });
                            }}
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            onClick={() => {
                              setNotes('');
                              setRejectOpen({ open: true, row: r });
                            }}
                          >
                            Reject
                          </Button>
                        </>
                      )}
                      {r.error_message && (
                        <Typography variant="caption" color="error" display="block">
                          {r.error_message}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={approveOpen.open} onClose={() => setApproveOpen({ open: false, row: null })} fullWidth maxWidth="sm">
        <DialogTitle>Approve backup deletion</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Review the short summary below, then confirm removal of the backup folder from the worker.
          </Typography>
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'action.hover' }}>
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Short view
            </Typography>
            <Stack spacing={0.75}>
              <Stack direction="row" spacing={1} alignItems="baseline" sx={{ flexWrap: 'wrap' }}>
                <Typography component="span" variant="caption" color="text.secondary" sx={{ minWidth: 100 }}>
                  Folder
                </Typography>
                <Typography component="span" variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {approveOpen.row?.folder_name}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="baseline">
                <Typography component="span" variant="caption" color="text.secondary" sx={{ minWidth: 100 }}>
                  Status
                </Typography>
                <Typography component="span" variant="body2" sx={{ fontWeight: 500 }}>
                  {approveOpen.row?.status}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="baseline" sx={{ flexWrap: 'wrap' }}>
                <Typography component="span" variant="caption" color="text.secondary" sx={{ minWidth: 100 }}>
                  Requester
                </Typography>
                <Typography component="span" variant="body2" sx={{ fontWeight: 500 }}>
                  {approveOpen.row?.requester_email || '—'}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ flexWrap: 'wrap' }}>
                <Typography component="span" variant="caption" color="text.secondary" sx={{ minWidth: 100, pt: 0.25 }}>
                  Reason
                </Typography>
                <Typography component="span" variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
                  {approveOpen.row?.reason || '—'}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="baseline">
                <Typography component="span" variant="caption" color="text.secondary" sx={{ minWidth: 100 }}>
                  Created
                </Typography>
                <Typography component="span" variant="body2" sx={{ fontWeight: 500 }}>
                  {fmt(approveOpen.row?.created_at)}
                </Typography>
              </Stack>
            </Stack>
          </Paper>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" sx={{ mb: 1 }}>
            This permanently removes folder <strong>{approveOpen.row?.folder_name}</strong> from the backup worker (per policy).
          </Typography>
          <TextField
            label="Review notes (optional)"
            fullWidth
            multiline
            minRows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveOpen({ open: false, row: null })}>Cancel</Button>
          <Button variant="contained" color="success" onClick={approve}>
            Approve delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rejectOpen.open} onClose={() => setRejectOpen({ open: false, row: null })} fullWidth maxWidth="sm">
        <DialogTitle>Reject request</DialogTitle>
        <DialogContent>
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'action.hover' }}>
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Short view
            </Typography>
            <Stack spacing={0.75}>
              <Stack direction="row" spacing={1} alignItems="baseline" sx={{ flexWrap: 'wrap' }}>
                <Typography component="span" variant="caption" color="text.secondary" sx={{ minWidth: 100 }}>
                  Folder
                </Typography>
                <Typography component="span" variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {rejectOpen.row?.folder_name}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="baseline" sx={{ flexWrap: 'wrap' }}>
                <Typography component="span" variant="caption" color="text.secondary" sx={{ minWidth: 100 }}>
                  Requester
                </Typography>
                <Typography component="span" variant="body2">
                  {rejectOpen.row?.requester_email || '—'}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ flexWrap: 'wrap' }}>
                <Typography component="span" variant="caption" color="text.secondary" sx={{ minWidth: 100, pt: 0.25 }}>
                  Reason
                </Typography>
                <Typography component="span" variant="body2" sx={{ wordBreak: 'break-word' }}>
                  {rejectOpen.row?.reason || '—'}
                </Typography>
              </Stack>
            </Stack>
          </Paper>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Optional notes are recorded with the rejection.
          </Typography>
          <TextField
            label="Review notes (optional)"
            fullWidth
            multiline
            minRows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen({ open: false, row: null })}>Cancel</Button>
          <Button variant="contained" color="error" onClick={reject}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
